import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useSEO } from '../../hooks/useSEO';
import { propertiesApi, wilayasApi, favoritesApi } from '../../lib/api';
import { PropertyCard } from '../../components/property/PropertyCard';
import { PropertyMap } from '../../components/map/PropertyMap';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Search, SlidersHorizontal, Map, List, X, ChevronDown } from 'lucide-react';

// Detect mobile for default view mode
const getDefaultViewMode = () => window.innerWidth >= 1024 ? 'split' : 'list';

export const SearchPage = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState([]);
  const [wilayas, setWilayas] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [viewMode, setViewMode] = useState(getDefaultViewMode); // list, map, split

  // Filters
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    transaction_type: searchParams.get('transaction_type') || '',
    property_type: searchParams.get('property_type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_surface: searchParams.get('min_surface') || '',
    max_surface: searchParams.get('max_surface') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  const [tempFilters, setTempFilters] = useState(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    loadWilayas();
    if (isAuthenticated) {
      loadFavorites();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadProperties();
  }, [filters, page]);

  const loadWilayas = async () => {
    try {
      const response = await wilayasApi.list();
      setWilayas(response.data || []);
    } catch (error) {
      console.error('Failed to load wilayas:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await favoritesApi.listIds();
      setFavoriteIds(response.data || []);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const loadProperties = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== 'all')
        ),
      };
      
      const response = await propertiesApi.list(params);
      setProperties(response.data.properties || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    setFiltersOpen(false);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(tempFilters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    const emptyFilters = {
      city: '',
      transaction_type: '',
      property_type: '',
      min_price: '',
      max_price: '',
      min_surface: '',
      max_surface: '',
      bedrooms: '',
      sort_by: 'created_at',
      sort_order: 'desc',
    };
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setPage(1);
    setSearchParams({});
  };

  const activeFiltersCount = Object.entries(filters).filter(
    ([key, value]) => value && value !== '' && value !== 'all' && !['sort_by', 'sort_order'].includes(key)
  ).length;

  const [cityInput, setCityInput] = useState(filters.city || '');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  const normalize = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const filteredWilayas = wilayas.filter(w => {
    const q = normalize(cityInput);
    return normalize(w.name).includes(q) || (w.name_ar && w.name_ar.includes(cityInput));
  }).slice(0, 8);

  const handleCitySelect = (name) => {
    setCityInput(name);
    setFilters({ ...filters, city: name });
    setShowCitySuggestions(false);
    setPage(1);
  };

  const handleCityClear = () => {
    setCityInput('');
    setFilters({ ...filters, city: '' });
    setShowCitySuggestions(false);
    setPage(1);
  };

  const propertyTypes = [
    { value: 'appartement', label: language === 'fr' ? 'Appartement' : 'شقة' },
    { value: 'maison', label: language === 'fr' ? 'Maison' : 'منزل' },
    { value: 'villa', label: language === 'fr' ? 'Villa' : 'فيلا' },
    { value: 'terrain', label: language === 'fr' ? 'Terrain' : 'أرض' },
    { value: 'commercial', label: language === 'fr' ? 'Local commercial' : 'محل تجاري' },
    { value: 'bureau', label: language === 'fr' ? 'Bureau' : 'مكتب' },
  ];

  const sortOptions = [
    { value: 'created_at-desc', label: t('search_newest') },
    { value: 'price-asc', label: t('search_price_asc') },
    { value: 'price-desc', label: t('search_price_desc') },
    { value: 'surface-desc', label: t('search_surface_desc') },
  ];

  const handleSort = (value) => {
    const [sort_by, sort_order] = value.split('-');
    setFilters({ ...filters, sort_by, sort_order });
  };

  const cityFilter = filters.city || '';
  const typeFilter = filters.transaction_type || '';
  const searchSeoTitle = language === 'fr'
    ? `Recherche immobiliere${cityFilter ? ` a ${cityFilter}` : ''} | Beytam`
    : `بحث عقاري${cityFilter ? ` في ${cityFilter}` : ''} | بيتم`;
  const searchSeoDesc = language === 'fr'
    ? `${total} annonces immobilieres${cityFilter ? ` a ${cityFilter}` : ' en Algerie'}. Comparez les prix et trouvez votre bien.`
    : `${total} إعلان عقاري${cityFilter ? ` في ${cityFilter}` : ' في الجزائر'}. قارن الأسعار واعثر على عقارك.`;

  useSEO({
    title: searchSeoTitle,
    description: searchSeoDesc,
    ogTitle: searchSeoTitle,
    ogDescription: searchSeoDesc,
    canonical: window.location.href,
  });

  return (
    <div className="min-h-screen bg-background" data-testid="search-page">
      {/* Header */}
      <div className="sticky top-16 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Quick filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <Select 
                value={filters.transaction_type || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, transaction_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-32" data-testid="filter-transaction">
                  <SelectValue placeholder={language === 'fr' ? 'Type' : 'النوع'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tout' : 'الكل'}</SelectItem>
                  <SelectItem value="vente">{t('search_buy')}</SelectItem>
                  <SelectItem value="location">{t('search_rent')}</SelectItem>
                </SelectContent>
              </Select>

              <div className="relative">
                <Input
                  type="text"
                  value={cityInput}
                  onChange={(e) => {
                    setCityInput(e.target.value);
                    setShowCitySuggestions(true);
                    if (!e.target.value) {
                      setFilters({ ...filters, city: '' });
                      setPage(1);
                    }
                  }}
                  onFocus={() => setShowCitySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                  placeholder={language === 'fr' ? 'Tapez une ville...' : 'اكتب مدينة...'}
                  className="w-44 h-10"
                  data-testid="filter-city-input"
                />
                {cityInput && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onMouseDown={(e) => { e.preventDefault(); handleCityClear(); }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                {showCitySuggestions && cityInput.length > 0 && filteredWilayas.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50 max-h-60 overflow-y-auto" data-testid="search-city-suggestions">
                    {filteredWilayas.map((w) => (
                      <button
                        key={w.name}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors flex items-center justify-between"
                        onMouseDown={(e) => { e.preventDefault(); handleCitySelect(w.name); }}
                      >
                        <span>{w.name}</span>
                        {w.name_ar && <span className="text-xs text-slate-400" dir="rtl">{w.name_ar}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Select 
                value={filters.property_type || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, property_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-40" data-testid="filter-property-type">
                  <SelectValue placeholder={t('search_type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous types' : 'جميع الأنواع'}</SelectItem>
                  {propertyTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {/* More filters */}
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="more-filters-btn">
                    <SlidersHorizontal className="w-4 h-4" />
                    {t('search_filters')}
                    {activeFiltersCount > 0 && (
                      <Badge className="bg-primary-500 text-white ml-1">{activeFiltersCount}</Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[400px] sm:max-w-[400px]">
                  <SheetHeader>
                    <SheetTitle>{t('search_filters')}</SheetTitle>
                  </SheetHeader>
                  <div className="py-6 space-y-6">
                    {/* Price range */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('search_price')} (DA)</label>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          type="number"
                          placeholder="Min"
                          value={tempFilters.min_price}
                          onChange={(e) => setTempFilters({ ...tempFilters, min_price: e.target.value })}
                        />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={tempFilters.max_price}
                          onChange={(e) => setTempFilters({ ...tempFilters, max_price: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Surface range */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('search_surface')} (m²)</label>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          type="number"
                          placeholder="Min"
                          value={tempFilters.min_surface}
                          onChange={(e) => setTempFilters({ ...tempFilters, min_surface: e.target.value })}
                        />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={tempFilters.max_surface}
                          onChange={(e) => setTempFilters({ ...tempFilters, max_surface: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Bedrooms */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('search_bedrooms')}</label>
                      <Select 
                        value={tempFilters.bedrooms || 'all'} 
                        onValueChange={(v) => setTempFilters({ ...tempFilters, bedrooms: v === 'all' ? '' : v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{language === 'fr' ? 'Peu importe' : 'غير محدد'}</SelectItem>
                          {[1, 2, 3, 4, 5].map((num) => (
                            <SelectItem key={num} value={num.toString()}>{num}+</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button variant="outline" onClick={clearFilters} className="flex-1">
                        {language === 'fr' ? 'Réinitialiser' : 'إعادة تعيين'}
                      </Button>
                      <Button onClick={applyFilters} className="flex-1 bg-primary-500">
                        {language === 'fr' ? 'Appliquer' : 'تطبيق'}
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* View mode toggle - Desktop */}
              <div className="hidden lg:flex items-center border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className={`rounded-none ${viewMode === 'list' ? 'bg-primary-500' : ''}`}
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'split' ? 'default' : 'ghost'}
                  size="sm"
                  className={`rounded-none ${viewMode === 'split' ? 'bg-primary-500' : ''}`}
                  onClick={() => setViewMode('split')}
                  data-testid="view-split-btn"
                >
                  <Map className="w-4 h-4 mr-1" />
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  className={`rounded-none ${viewMode === 'map' ? 'bg-primary-500' : ''}`}
                  onClick={() => setViewMode('map')}
                  data-testid="view-map-btn"
                >
                  <Map className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Results count & sort */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{total}</span> {t('search_results')}
            </p>
            <Select 
              value={`${filters.sort_by}-${filters.sort_order}`}
              onValueChange={handleSort}
            >
              <SelectTrigger className="w-48" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${viewMode === 'split' ? 'lg:flex h-auto lg:h-[calc(100vh-220px)]' : ''}`}>
        {/* Property list */}
        {(viewMode === 'list' || viewMode === 'split') && (
          <div className={`${viewMode === 'split' ? 'w-full lg:w-[55%] lg:overflow-y-auto px-4 py-6' : 'container mx-auto px-4 py-8'}`}>
            {loading ? (
              <div className={`grid gap-6 ${viewMode === 'split' ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="aspect-[4/3] rounded-lg" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="text-center py-20">
                <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {t('common_no_results')}
                </h3>
                <p className="text-slate-600 mb-6">
                  {language === 'fr' 
                    ? 'Essayez de modifier vos critères de recherche' 
                    : 'حاول تعديل معايير البحث الخاصة بك'}
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  {language === 'fr' ? 'Réinitialiser les filtres' : 'إعادة تعيين الفلاتر'}
                </Button>
              </div>
            ) : (
              <>
                <div className={`grid gap-6 ${viewMode === 'split' ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                  {properties.map((property) => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      isFavorite={favoriteIds.includes(property.id)}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      {t('common_previous')}
                    </Button>
                    <span className="flex items-center px-4 text-sm text-slate-600">
                      {page} / {pages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page >= pages}
                      onClick={() => setPage(page + 1)}
                    >
                      {t('common_next')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Map (for split/map view) */}
        {(viewMode === 'map' || viewMode === 'split') && (
          <div 
            className={`${
              viewMode === 'split' 
                ? 'hidden lg:block w-[45%] h-full border-l border-slate-200' 
                : 'h-[calc(100vh-220px)]'
            }`}
            data-testid="map-container"
          >
            <PropertyMap properties={properties} height="100%" />
          </div>
        )}
      </div>

      {/* Mobile floating toggle - List/Map */}
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50" data-testid="mobile-view-toggle">
        <button
          onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
          className="flex items-center gap-2 bg-blue-900 text-white px-5 py-3 rounded-full shadow-lg hover:bg-blue-800 transition-colors"
        >
          {viewMode === 'map' ? (
            <>
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'fr' ? 'Liste' : 'القائمة'}</span>
            </>
          ) : (
            <>
              <Map className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'fr' ? 'Carte' : 'الخريطة'}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
